interface CoupangSearchProduct {
  productPageUrl?: string;
  vendorItemId?: string;
}

export interface CoupangUrlSource {
  cpProductId: string;
  cpItemId: string;
  productPageUrl?: string;
  vendorItemId?: string;
  cpSearchedProduct?: CoupangSearchProduct;
}

const COUPANG_HOSTS = new Set(['www.coupang.com', 'coupang.com']);

function extractProductId(pathname: string): string | null {
  const match = pathname.match(/^\/vp\/products\/(\d+)$/);
  return match?.[1] ?? null;
}

function buildFallbackCoupangUrl(productId: string, itemId: string, vendorItemId?: string): string {
  const searchParams = new URLSearchParams();

  searchParams.set('itemId', itemId);

  if (vendorItemId) {
    searchParams.set('vendorItemId', vendorItemId);
  }

  const queryString = searchParams.toString();
  return `https://www.coupang.com/vp/products/${productId}?${queryString}`;
}

function normalizeStoredCoupangUrl(
  rawUrl: string | undefined,
  expectedProductId: string,
  expectedItemId: string,
  fallbackVendorItemId?: string,
): string | null {
  if (!rawUrl) {
    return null;
  }

  try {
    const url = new URL(rawUrl);
    if (!COUPANG_HOSTS.has(url.hostname)) {
      return null;
    }

    const productId = extractProductId(url.pathname);
    const itemId = url.searchParams.get('itemId');
    if (!productId || !itemId) {
      return null;
    }

    if (productId !== expectedProductId) {
      return null;
    }

    if (itemId !== expectedItemId) {
      return null;
    }

    return buildFallbackCoupangUrl(productId, itemId, url.searchParams.get('vendorItemId') ?? fallbackVendorItemId);
  } catch {
    return null;
  }
}

export const buildCoupangProductUrl = (product: CoupangUrlSource): string => {
  const preferredVendorItemId = product.cpSearchedProduct?.vendorItemId ?? product.vendorItemId;
  const storedUrlCandidates = [product.cpSearchedProduct?.productPageUrl, product.productPageUrl];

  for (const storedUrlCandidate of storedUrlCandidates) {
    const storedUrl = normalizeStoredCoupangUrl(
      storedUrlCandidate,
      product.cpProductId,
      product.cpItemId,
      preferredVendorItemId,
    );

    if (storedUrl) {
      return storedUrl;
    }
  }

  return buildFallbackCoupangUrl(product.cpProductId, product.cpItemId, preferredVendorItemId);
};
